/**
 * Hariom ERP - Human Resources Module
 * Employee directory. Records are added/edited/deleted by HR and persisted.
 */
import React, { useState } from 'react'
import { Plus, Edit3, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useHRStore, type Employee } from '../../store/hrStore'
import FormModal, { type FormField } from '../../components/ui/FormModal'

const DEPTS = ['Management', 'Production', 'Sales', 'Quality', 'Finance', 'Inventory', 'HR', 'Marketing']
const STATUSES = ['active', 'on_leave', 'resigned', 'terminated']

const EMP_FIELDS: FormField[] = [
  { name: 'empNo',       label: 'Employee No', required: true, placeholder: 'e.g. HE-009' },
  { name: 'name',        label: 'Full Name', required: true, placeholder: 'e.g. Ramesh Kumar' },
  { name: 'designation', label: 'Designation', placeholder: 'e.g. Production Manager', full: true },
  { name: 'dept',        label: 'Department', type: 'select', options: DEPTS, required: true },
  { name: 'status',      label: 'Status', type: 'select', options: STATUSES },
  { name: 'phone',       label: 'Phone', placeholder: 'e.g. 91099-00000' },
  { name: 'joined',      label: 'Joining Date', type: 'date' },
]

const HR: React.FC = () => {
  const { items: employees, add, update, remove } = useHRStore()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Employee | null>(null)

  const depts = [...new Set(employees.map(e => e.dept))]
  const onLeave = employees.filter(e => e.status === 'on_leave').length

  const handleSave = (v: Record<string, string>) => {
    const empNo = v.empNo.trim()
    if (employees.some(e => e.empNo.toLowerCase() === empNo.toLowerCase() && e.id !== editing?.id)) {
      toast.error(`Employee No "${empNo}" already exists`); return
    }
    const record: Employee = {
      id: editing ? editing.id : `emp-${Date.now()}`,
      empNo, name: v.name.trim(), designation: v.designation.trim(),
      dept: v.dept || 'Management', status: v.status || 'active',
      phone: v.phone.trim(), joined: v.joined,
    }
    if (editing) { update(editing.id, record); toast.success(`Updated ${record.name}`) }
    else { add(record); toast.success(`Added ${record.name}`) }
    setModalOpen(false); setEditing(null)
  }

  const handleDelete = (e: Employee) => {
    if (window.confirm(`Remove ${e.name}?`)) { remove(e.id); toast.success(`Removed ${e.name}`) }
  }

  const toRecord = (e: Employee): Record<string, string> => ({
    empNo: e.empNo, name: e.name, designation: e.designation, dept: e.dept,
    status: e.status, phone: e.phone, joined: e.joined,
  })

  const statusBadge = (s: string) =>
    s === 'active' ? 'badge-success'
    : s === 'on_leave' ? 'badge-warning'
    : 'bg-steel-100 text-steel-600 text-xs px-2 py-0.5 rounded-full'

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Human Resources</h1>
          <p className="page-subtitle">Employee directory, attendance &amp; payroll management</p>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true) }} className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> Add Employee</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Total Employees</p><p className="text-2xl font-bold">{employees.length}</p></div>
        <div className="card py-3 border-l-4 border-l-green-500"><p className="text-xs text-steel-500">Active</p><p className="text-2xl font-bold text-green-600">{employees.filter(e => e.status === 'active').length}</p></div>
        <div className="card py-3 border-l-4 border-l-purple-500"><p className="text-xs text-steel-500">Departments</p><p className="text-2xl font-bold">{depts.length}</p></div>
        <div className="card py-3 border-l-4 border-l-yellow-500"><p className="text-xs text-steel-500">On Leave</p><p className="text-2xl font-bold text-yellow-600">{onLeave}</p></div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-steel-50 border-b border-steel-200">
              <th className="table-header">Emp No</th><th className="table-header">Name</th>
              <th className="table-header">Designation</th><th className="table-header">Department</th>
              <th className="table-header">Phone</th><th className="table-header">Joined</th>
              <th className="table-header text-center">Status</th><th className="table-header text-center">Actions</th>
            </tr></thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-steel-400 text-sm py-10">
                  No employees yet. Click <span className="font-semibold text-steel-600">“Add Employee”</span> to add one.
                </td></tr>
              ) : employees.map(e => (
                <tr key={e.id} className="table-row-hover">
                  <td className="table-cell font-mono text-xs font-medium text-primary-700">{e.empNo}</td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-700 text-xs font-bold">{e.name.charAt(0)}</span>
                      </div>
                      <span className="font-medium text-steel-800 text-sm">{e.name}</span>
                    </div>
                  </td>
                  <td className="table-cell text-sm">{e.designation || '—'}</td>
                  <td className="table-cell"><span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{e.dept}</span></td>
                  <td className="table-cell text-xs text-steel-500">{e.phone || '—'}</td>
                  <td className="table-cell text-xs text-steel-500">{e.joined || '—'}</td>
                  <td className="table-cell text-center"><span className={statusBadge(e.status)}>{e.status.replace('_', ' ')}</span></td>
                  <td className="table-cell text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => { setEditing(e); setModalOpen(true) }} className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={14}/></button>
                      <button onClick={() => handleDelete(e)} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <FormModal
          title={editing ? 'Edit Employee' : 'Add Employee'}
          submitLabel={editing ? 'Save Changes' : 'Add Employee'}
          fields={EMP_FIELDS}
          initial={editing ? toRecord(editing) : {}}
          onClose={() => { setModalOpen(false); setEditing(null) }}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default HR
